// Classificatore deterministico di UNA riga import (DDT/fattura). PURO — nessun I/O.
// Copia Deno mirror in supabase/functions/_shared/validateLine.ts — TENERE ALLINEATO.

export type LineGateReason =
  | 'nuovo_alimento'
  | 'lotto_sospetto'
  | 'unita_incoerente'
  | 'costo_pezzo_alto'
  | 'bassa_confidenza';

export interface LineValidation {
  status: 'ok' | 'da_confermare';
  reasons: LineGateReason[];
  suggested_uom: string | null;
  lot_is_placeholder: boolean;
}

export interface ValidateLineInput {
  matchedFoodId: string | null;
  matchConfidence: number | null;
  name: string;
  category: string | null;
  uom: string | null;
  batchLot: string | null;
  qty: number | null;
  unitPrice: number | null;
  aiLineConfidence: number | null;
  // T-460 — UdM base del food AGGANCIATO (per la coerenza food↔documento) e UdM
  // effettivamente caricata dalla riga documento (per il costo al singolo pezzo).
  // Opzionali: i re-check che non le hanno (commit, re-validazione parziale)
  // semplicemente non sollevano i gate T-460.
  matchedFoodUom?: string | null;
  loadedUom?: string | null;
}

// Parole che indicano una NOTA nel campo lotto (non un codice reale).
const LOT_NOTE_WORDS = new Set<string>([
  'vedi', 'rif', 'ref', 'riferimento', 'fattura', 'fatt', 'ddt', 'bolla',
  'nessun', 'nessuno', 'senza', 'none', 'tbd',
]);
// Marker "vuoto/sconosciuto" (dopo aver tolto la punteggiatura).
const LOT_EMPTY_MARKERS = new Set<string>(['na', 'nd', 'tbd', 'xxx', 'none']);

/**
 * true = il valore lotto è una nota/placeholder, non un codice reale.
 * Il trattino singolo "-" è una convenzione esplicita "nessun lotto" → NON placeholder.
 * null/vuoto NON è placeholder qui (è "mancante", gestito da validateImportLine).
 */
export function isPlaceholderLot(raw: string | null | undefined): boolean {
  if (raw == null) return false;
  const s = raw.trim();
  if (s === '') return false;
  if (s === '-') return false; // convenzione "nessun lotto"
  const low = s.toLowerCase();
  const tokens = low.split(/[^a-z0-9à-ù]+/i).filter(Boolean);
  // 1) contiene una parola-nota come token intero
  if (tokens.some((t) => LOT_NOTE_WORDS.has(t))) return true;
  // 2) collassato senza punteggiatura = marker vuoto noto (n/a→na, n.d.→nd, …)
  const collapsed = low.replace(/[^a-z0-9]/g, '');
  if (LOT_EMPTY_MARKERS.has(collapsed)) return true;
  // 3) un solo carattere alfanumerico è normalmente rumore della colonna
  // adiacente (es. "M" o "0"), non un lotto tracciabile.
  if (/^[a-z0-9]$/i.test(collapsed)) return true;
  // 4) sequenza lunga dello stesso numero = placeholder generato/riempitivo
  // (incidente reale: "9999999999" salvato come lotto).
  if (/^(\d)\1{5,}$/.test(collapsed)) return true;
  // 5) sembra una frase: ha uno spazio E ≥2 parole alfabetiche di ≥3 lettere
  const alphaWords = tokens.filter((t) => /^[a-zà-ù]+$/i.test(t) && t.length >= 3);
  if (/\s/.test(s) && alphaWords.length >= 2) return true;
  // 6) nessun carattere alfanumerico (solo punteggiatura, es. "...", "—")
  if (!/[a-z0-9]/i.test(s)) return true;
  return false;
}

// Unità "a peso/volume" che NON possono valere per un articolo contato a pezzi.
const WEIGHT_VOLUME_UOMS = new Set<string>(['kg', 'g', 'l', 'ml']);
// Indizi (nel nome) di articolo contato a pezzi.
const COUNT_HINTS = [
  'lattina', 'lattine', 'bottiglia', 'bottiglie', 'can ',
  'coperchio', 'coppetta', 'sacchetto', 'scatola', 'carta', 'busta',
  'cucchiaino', 'vaschetta', 'plateau', 'coppette', 'bicchiere', 'cannuccia',
];

export function isSuspiciousUom(input: { name: string; category: string | null; uom: string | null }): { suspicious: boolean; suggested: string | null } {
  const uom = (input.uom ?? '').trim().toLowerCase();
  if (!uom) return { suspicious: false, suggested: null };
  if (!WEIGHT_VOLUME_UOMS.has(uom)) return { suspicious: false, suggested: null };
  const hay = `${input.name} ${input.category ?? ''}`.toLowerCase();
  const looksCounted = COUNT_HINTS.some((h) => hay.includes(h));
  if (looksCounted) return { suspicious: true, suggested: 'pz' };
  return { suspicious: false, suggested: null };
}

// Soglia sotto la quale l'AI si considera "incerta" sulla riga.
const AI_CONFIDENCE_MIN = 0.5;
// Prezzo unitario oltre il quale è quasi certamente un errore di lettura (€/unità).
const UNIT_PRICE_MAX = 1000;

// T-460 — Unità "a singolo pezzo" (un articolo = un pezzo). conf/ct/collo/cassa
// sono multi-pack: un prezzo alto lì è legittimo, quindi NON qui. Include tutte
// le sigle che uom.ts `canonicalUom()` mappa a "pz" (nr/n/num/cad/cadauno/ud/ea/
// pzi) — le fatture italiane usano spesso "NR"/"CAD"/"PZ." — così il gate non ha
// buchi. Il punto finale ("PZ.") viene normalizzato nel check.
const SINGLE_PIECE_UOMS = new Set<string>([
  'pz', 'pezzo', 'pezzi', 'pzi', 'nr', 'n', 'num', 'cad', 'cadauno', 'ud', 'ea',
]);
// Oltre questo €/pezzo un singolo pezzo di ALIMENTO è quasi sempre il prezzo del
// collo/cassa non ripartito (iper-valorizzazione food cost). Soglia volutamente
// lasca: un pezzo caro vero (forma di formaggio, prosciutto intero) la supera →
// l'operatore conferma con un tap, il carico non si blocca.
const PIECE_COST_MAX = 25;

export function validateImportLine(input: ValidateLineInput): LineValidation {
  const reasons: LineGateReason[] = [];

  // 1) nuovo alimento: nessun aggancio sicuro
  if (!input.matchedFoodId) reasons.push('nuovo_alimento');

  // 2) lotto: mancante o placeholder-frase
  const placeholder = isPlaceholderLot(input.batchLot);
  const missing = input.batchLot == null || input.batchLot.trim() === '';
  if (placeholder || missing) reasons.push('lotto_sospetto');

  // 3) unità incoerente
  const uom = isSuspiciousUom({ name: input.name, category: input.category, uom: input.uom });
  if (uom.suspicious) reasons.push('unita_incoerente');

  // 3b) T-460 — costo al singolo pezzo implausibile: un articolo caricato a
  //     pezzi con prezzo troppo alto per UN pezzo = quasi sempre il prezzo del
  //     collo/cassa non ripartito sul singolo pezzo (iper-valorizzazione food
  //     cost). Rete di sicurezza: applica il fattore-collo o conferma.
  const loaded = (input.loadedUom ?? input.uom ?? '').trim().toLowerCase().replace(/\.+$/, '');
  const loadedIsSinglePiece = SINGLE_PIECE_UOMS.has(loaded);
  if (loadedIsSinglePiece && input.unitPrice != null && input.unitPrice > PIECE_COST_MAX) {
    reasons.push('costo_pezzo_alto');
  }

  // 3c) T-460 — unità prodotto vs documento incoerente: il food AGGANCIATO
  //     esiste a peso/volume (kg/l) ma il documento lo carica a pezzi → senza un
  //     fattore di ripartizione il costo/lotto è sbagliato (es. "Coca Cola
  //     Lattina" anagrafata in litri, caricata a pz).
  const foodBase = (input.matchedFoodUom ?? '').trim().toLowerCase();
  if (loadedIsSinglePiece && WEIGHT_VOLUME_UOMS.has(foodBase) && !reasons.includes('unita_incoerente')) {
    reasons.push('unita_incoerente');
  }

  // 4) bassa confidenza: AI incerta O campi implausibili
  const badQty = input.qty == null || !(input.qty > 0);
  const badPrice = input.unitPrice != null && (input.unitPrice < 0 || input.unitPrice > UNIT_PRICE_MAX);
  const lowAi = input.aiLineConfidence != null && input.aiLineConfidence < AI_CONFIDENCE_MIN;
  if (badQty || badPrice || lowAi) reasons.push('bassa_confidenza');

  return {
    status: reasons.length === 0 ? 'ok' : 'da_confermare',
    reasons,
    suggested_uom: uom.suggested,
    lot_is_placeholder: placeholder,
  };
}
